import { useCallback, useEffect, useState } from 'react'
import {
  useGetPushConfigQuery,
  useRegisterPushSubscriptionMutation,
} from '../api/notifications'
import {
  cleanupWebPushSubscription,
  getPushPermission,
  isWebPushSupported,
  subscribeToWebPush,
} from '../utils/webPush'

export type WebPushSubscriptionState =
  | 'unsupported'
  | 'default'
  | 'denied'
  | 'enabled'
  | 'loading'

export function useWebPushSubscription() {
  const [state, setState] = useState<WebPushSubscriptionState>('loading')
  const { data: pushConfig } = useGetPushConfigQuery(undefined, {
    skip: !isWebPushSupported(),
  })
  const [registerPush] = useRegisterPushSubscriptionMutation()

  const syncState = useCallback(async () => {
    if (!isWebPushSupported()) {
      setState('unsupported')
      return
    }

    const permission = getPushPermission()
    if (permission === 'denied') {
      setState('denied')
      return
    }

    if (permission === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          setState('enabled')
          return
        }
      } catch {
        // fall through to default
      }
    }

    setState('default')
  }, [])

  useEffect(() => {
    syncState()
  }, [syncState])

  const enablePush = useCallback(async () => {
    if (!isWebPushSupported() || !pushConfig?.enabled || !pushConfig.vapid_public_key) {
      setState('unsupported')
      return
    }

    setState('loading')

    try {
      const permission = await Notification.requestPermission()
      if (permission === 'denied') {
        setState('denied')
        return
      }
      if (permission !== 'granted') {
        setState('default')
        return
      }

      const subscription = await subscribeToWebPush(pushConfig.vapid_public_key)
      if (!subscription.endpoint || !subscription.keys?.p256dh || !subscription.keys?.auth) {
        setState('default')
        return
      }

      await registerPush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subscription.keys.p256dh,
          auth: subscription.keys.auth,
        },
      }).unwrap()

      setState('enabled')
    } catch {
      await syncState()
    }
  }, [pushConfig, registerPush, syncState])

  const disablePush = useCallback(async () => {
    setState('loading')
    try {
      await cleanupWebPushSubscription()
    } catch {
      // best effort
    }
    setState('default')
  }, [])

  return { state, enablePush, disablePush }
}
